import type { Session } from "@supabase/supabase-js";
import { createContext, type PropsWithChildren, useContext, useEffect, useState } from "react";
import { appConfig } from "../lib/config";
import { supabase } from "../lib/supabase";

type AuthValue = {
  session: Session | null;
  loading: boolean;
  demo: boolean;
  signOut: () => Promise<void>;
};
const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(!appConfig.demoMode);
  useEffect(() => {
    if (appConfig.demoMode || !supabase) {
      setLoading(false);
      return;
    }
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, next) => setSession(next));
    return () => data.subscription.unsubscribe();
  }, []);
  return (
    <AuthContext.Provider
      value={{
        session,
        loading,
        demo: appConfig.demoMode,
        signOut: async () => {
          if (supabase) await supabase.auth.signOut();
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
